import { getColorForString } from "@/features/utils/colors";
import type { SessionClassification, SessionTags } from "./types";

/**
 * One colour per classification, fixed rather than hashed from the tag name, and
 * the same wherever a classification is drawn: the dashboard's donut and legend,
 * the calls table, the conversation header. The hash gave "Information" and
 * "Réservation" the same orange, and put the chart's booking colour on the table's
 * cancellations.
 *
 * The order is the reading order — what the agent did, from the most to the least
 * engaged outcome — and the dashboard uses it for both the slices and the legend so
 * a category always sits in the same place.
 */
export const CLASSIFICATIONS: { id: SessionClassification; color: string }[] = [
  { id: "booking_creation", color: "#57BCFF" },
  { id: "booking_reschedule", color: "#9076F7" },
  { id: "booking_cancellation", color: "#F13FAA" },
  { id: "transfer", color: "#00BFA6" },
  { id: "qualification_form", color: "#FFA415" },
  { id: "other", color: "#94A3B8" },
  { id: "no_answer", color: "#64748B" },
];

const BY_ID = new Map<string, string>(
  CLASSIFICATIONS.map(({ id, color }) => [id, color]),
);

/** The colour of a classification; a tag the list does not know yet gets a stable hashed one. */
export const classificationColor = (tag: string): string =>
  BY_ID.get(tag) ?? getColorForString(tag);

/**
 * The tags of a session worth showing, in the classification order: the ones the
 * back-end still writes. Older rows may carry `booking`, `information` or `abandoned`,
 * which said nothing the result and the other tags do not.
 */
export const shownTags = (tags?: string[] | null): SessionTags[] =>
  CLASSIFICATIONS.map(({ id }) => id).filter(
    (id): id is SessionTags =>
      id !== "other" && id !== "no_answer" && !!tags?.includes(id),
  );
